import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useParams } from "react-router-dom";
import { useAuthInfo } from "../allauth/auth/hooks";
import ApiClient, { HttpResponse } from "../config/ApiClient";
import type { AthletePaymentAssignment, LessonPlanRole, USER_TYPE } from "../object-actions/types/types";
import { getParticipationRole } from "../utils/onboarding";
import { pickPrimaryMembershipRole } from "../utils/membershipRoles";
import { useAppContext } from "./AppContext";

export type FocusedAssignmentRouteKey =
    | { kind: "assignment_id"; id: number }
    | { kind: "athlete_id"; id: number };

export type AssignmentAfterLoadCallback = (
    assignment: AthletePaymentAssignment,
    paymentAssignmentId: number,
) => void | Promise<void>;

export function buildAthleteAssignmentDetailPath(key: FocusedAssignmentRouteKey): string {
    return key.kind === "assignment_id"
        ? `/api/athlete-assignments-by-id/${key.id}/`
        : `/api/athlete-assignments/${key.id}/`;
}

export async function fetchAthletePaymentAssignmentByKey(
    key: FocusedAssignmentRouteKey,
): Promise<HttpResponse<AthletePaymentAssignment>> {
    return ApiClient.get<AthletePaymentAssignment>(buildAthleteAssignmentDetailPath(key));
}

function parseRouteKey(
    assignmentIdParam: string | undefined,
    athleteIdParam: string | undefined,
): FocusedAssignmentRouteKey | null {
    if (assignmentIdParam) {
        const parsed = parseInt(assignmentIdParam, 10);
        if (!Number.isNaN(parsed)) return { kind: "assignment_id", id: parsed };
    }
    if (athleteIdParam) {
        const parsed = parseInt(athleteIdParam, 10);
        if (!Number.isNaN(parsed)) return { kind: "athlete_id", id: parsed };
    }
    return null;
}

function paymentAssignmentIdFrom(assignment: AthletePaymentAssignment): number {
    return assignment.paymentassignment_id ?? assignment.id;
}

function participationRoleToUserType(role: LessonPlanRole | null): USER_TYPE | null {
    if (!role) return null;
    return role;
}

interface FocusedAssignmentContextType {
    /** Parsed from the current route, if any. */
    routeKey: FocusedAssignmentRouteKey | null;
    /** Payment assignment id from the route or loaded assignment. */
    assignmentId: number | null;
    assignment: AthletePaymentAssignment | null;
    participationRole: LessonPlanRole | null;
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
    /** Run after each successful load for this route key; return unsubscribe. */
    registerAfterLoad: (callback: AssignmentAfterLoadCallback) => () => void;
}

const FocusedAssignmentContext = createContext<FocusedAssignmentContextType | null>(null);

export function useFocusedAssignment() {
    const ctx = useContext(FocusedAssignmentContext);
    if (!ctx) {
        throw new Error("useFocusedAssignment must be used within FocusedAssignmentProvider");
    }
    return ctx;
}

/** Optional access when the provider may be absent (tests). */
export function useFocusedAssignmentOptional() {
    return useContext(FocusedAssignmentContext);
}

interface Props {
    children: ReactNode;
}

export function FocusedAssignmentProvider({ children }: Props) {
    const { isAuthenticated } = useAuthInfo();
    const { membership, setCurrentRole } = useAppContext();
    const { assignment_id: assignmentIdParam, athlete_id: athleteIdParam } = useParams<{
        assignment_id?: string;
        athlete_id?: string;
    }>();

    const routeKey = useMemo(
        () => parseRouteKey(assignmentIdParam, athleteIdParam),
        [assignmentIdParam, athleteIdParam],
    );

    const [assignment, setAssignment] = useState<AthletePaymentAssignment | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const afterLoadRef = useRef<Set<AssignmentAfterLoadCallback>>(new Set());

    const registerAfterLoad = useCallback((callback: AssignmentAfterLoadCallback) => {
        afterLoadRef.current.add(callback);
        return () => {
            afterLoadRef.current.delete(callback);
        };
    }, []);

    const syncCurrentRoleForAssignment = useCallback(
        (next: AthletePaymentAssignment | null) => {
            if (next) {
                const participation = participationRoleToUserType(getParticipationRole(next.my_roles));
                if (participation) {
                    setCurrentRole(participation);
                    return;
                }
            }
            setCurrentRole(pickPrimaryMembershipRole(membership?.roles ?? null));
        },
        [membership?.roles, setCurrentRole],
    );

    const runAfterLoadCallbacks = useCallback(
        async (loaded: AthletePaymentAssignment) => {
            const paymentAssignmentId = paymentAssignmentIdFrom(loaded);
            const callbacks = [...afterLoadRef.current];
            await Promise.all(
                callbacks.map((cb) => Promise.resolve(cb(loaded, paymentAssignmentId))),
            );
        },
        [],
    );

    const refresh = useCallback(async () => {
        if (!isAuthenticated || !routeKey) {
            setAssignment(null);
            setError(null);
            setLoading(false);
            syncCurrentRoleForAssignment(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetchAthletePaymentAssignmentByKey(routeKey);
            if (response.success && response.data) {
                setAssignment(response.data);
                syncCurrentRoleForAssignment(response.data);
                await runAfterLoadCallbacks(response.data);
            } else {
                setAssignment(null);
                syncCurrentRoleForAssignment(null);
                setError(response.error || "Failed to load assignment");
            }
        } catch (err) {
            console.error("[FOCUSED_ASSIGNMENT] Failed to load assignment", err);
            setAssignment(null);
            syncCurrentRoleForAssignment(null);
            setError(err instanceof Error ? err.message : "Failed to load assignment");
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, routeKey, syncCurrentRoleForAssignment, runAfterLoadCallbacks]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        const handler = () => refresh();
        window.addEventListener("smsp:waiver-accepted", handler);
        return () => window.removeEventListener("smsp:waiver-accepted", handler);
    }, [refresh]);

    const assignmentId = useMemo(() => {
        if (routeKey?.kind === "assignment_id") return routeKey.id;
        if (assignment) return paymentAssignmentIdFrom(assignment);
        return null;
    }, [routeKey, assignment]);

    const participationRole = useMemo(
        () => getParticipationRole(assignment?.my_roles),
        [assignment?.my_roles],
    );

    const value: FocusedAssignmentContextType = useMemo(
        () => ({
            routeKey,
            assignmentId,
            assignment,
            participationRole,
            loading,
            error,
            refresh,
            registerAfterLoad,
        }),
        [
            routeKey,
            assignmentId,
            assignment,
            participationRole,
            loading,
            error,
            refresh,
            registerAfterLoad,
        ],
    );

    return (
        <FocusedAssignmentContext.Provider value={value}>
            {children}
        </FocusedAssignmentContext.Provider>
    );
}
